/** One home for hrefs INTO console grids and detail pages.
 *
 * Every "open this record" / "filter that grid by this value" link the console
 * generates goes through here, so the id-space knowledge lives in one place
 * and `gridLinks.test.ts` can prove that each generated path is a real route
 * and each `?q=` condition parses under the target grid's own namespaces.
 *
 * Grammar recap (see aidocs/plans/2026-09-29-detail-page-links.md):
 *   - QuerySearch grids take `?q=<DSL>` — conditions joined by ` & `,
 *     serialised with the canonical `serialize()` so quoting is never
 *     re-derived — plus `?tenant=<id>` for the picker.
 *   - `useGridFilters` grids take plain params.
 *   - /delivery-logs, /api-requests and /external-access need a bounded
 *     `created` window: an explicit `?q=` suppresses the window the page would
 *     otherwise seed, and the api-requests / external-access backends refuse a
 *     windowless search outright. Their builders below add one when the caller
 *     gave none.
 *
 * Id spaces that look right and are wrong — never build these:
 *   - /delivery-logs "same booking" is `participant=<console pk>`; `booking=`
 *     is the booking-SYSTEM id string.
 *   - /bookings `gtu=` is the pk; `id=` is the custom_id.
 *   - /delivery-logs `tag=` is the redelivery LOG tag, not a Group_Tour_Tag.
 *   - /tours `trip_type=` is the tour/bus/exhibition discriminator, not the
 *     Trip_Type catalog entity.
 *   - `Import_Run.task_id` is the Celery id, not a BackgroundTaskRecord id.
 */
import { serialize, type FilterNode, type QueryOperator } from '@/components/QuerySearch';
import { createdWindowNode, hasCreatedTimeBound } from '@/features/delivery-logs/createdWindow';

export { createdWindowNode, hasCreatedTimeBound };

// ---- Conditions -----------------------------------------------------------

/** `ns=value` (case-insensitive equality unless told otherwise). */
export function cond(namespace: string, value: string, operator: QueryOperator = 'iexact'): FilterNode {
  return { kind: 'cond', namespace, operator, value };
}

/** `ns=#<id>` — the id-match form, which pins ONE row where the plain
 *  equality would match every same-named record (tags, tours, users, …).
 *  `idmatch` is the widget's FE-only node operator for the `=#` sigil (the
 *  wire sends `exact`); the parser offers it only where a namespace declares
 *  BOTH `exact` and `iexact` — the test checks every use. Namespaces that are
 *  exact-only (delivery-logs `participant`, import-runs `request`) take
 *  `cond(ns, value, 'exact')` instead. */
export function idCond(namespace: string, id: number | string): FilterNode {
  return { kind: 'cond', namespace, operator: 'idmatch', value: String(id) };
}

/** `ns=<from>..<to>`. */
export function rangeCond(namespace: string, from: string, to: string): FilterNode {
  return { kind: 'cond', namespace, operator: 'range', value: [from, to] };
}

/** ISO timestamp → the widget's naive-UTC `yyyy-MM-dd HH:mm` bound. Minute
 *  precision: seconds would just be noise in the query box. */
function utcBound(d: Date): string {
  return d.toISOString().slice(0, 16).replace('T', ' ');
}

/** `ns` within ±`ms` around `iso`, as an editable range chip. */
export function aroundCond(namespace: string, iso: string, ms: number): FilterNode {
  const t = new Date(iso).getTime();
  return rangeCond(namespace, utcBound(new Date(t - ms)), utcBound(new Date(t + ms)));
}

/** `ns` from `beforeMs` before `iso` to `afterMs` after it — what FOLLOWED a known
 *  moment (the deliveries of a save), without what came days before it. */
export function sinceCond(namespace: string, iso: string, afterMs: number, beforeMs = 60_000): FilterNode {
  const t = new Date(iso).getTime();
  return rangeCond(namespace, utcBound(new Date(t - beforeMs)), utcBound(new Date(t + afterMs)));
}

/** Any of `nodes` (`a | b`); a single node stands alone (canonical trees never hold a
 *  one-child group). */
export function anyOf(nodes: FilterNode[]): FilterNode {
  return nodes.length === 1 ? nodes[0]! : { kind: 'group', op: 'OR', children: nodes };
}

/** All of `nodes` (`a & b`) as ONE node — an alternative of `anyOf`. */
export function allOf(nodes: FilterNode[]): FilterNode {
  return nodes.length === 1 ? nodes[0]! : { kind: 'group', op: 'AND', children: nodes };
}

/** `created` within ±`hours` around `ts` — the api-requests default when a
 *  reference moment is known (the webhook that produced an import run landed
 *  moments before the run's created_ts). */
export function createdAroundNode(ts: string, hours = 1): FilterNode {
  return aroundCond('created', ts, hours * 3_600_000);
}

/** The ±delta presets a timestamp value offers (shared with TimestampsCell). */
export const TIME_DELTAS: { label: string; tooltip: string; ms: number }[] = [
  { label: '±5m', tooltip: '±5 minutes', ms: 5 * 60_000 },
  { label: '±1h', tooltip: '±1 hour', ms: 3_600_000 },
  { label: '±12h', tooltip: '±12 hours', ms: 12 * 3_600_000 },
  { label: '±1d', tooltip: '±1 day', ms: 24 * 3_600_000 },
  { label: '±7d', tooltip: '±7 days', ms: 7 * 24 * 3_600_000 },
];

// ---- URL assembly ---------------------------------------------------------

export type Params = Record<string, string | number | boolean | null | undefined>;

function appendParams(usp: URLSearchParams, params: Params | undefined): void {
  for (const [k, v] of Object.entries(params ?? {})) {
    if (v === null || v === undefined || v === '') continue;
    usp.set(k, String(v));
  }
}

/** `path?<params>` — null/undefined/empty values are dropped. */
export function plainHref(path: string, params?: Params): string {
  const usp = new URLSearchParams();
  appendParams(usp, params);
  const qs = usp.toString();
  return qs ? `${path}?${qs}` : path;
}

/** A tenant picker value: the numeric id, or the raw string a grid cell handed
 *  back (the embedded grids' `tenant` quick filter). Null/undefined = unscoped. */
export type Tenant = number | string | null | undefined;

export interface QueryHrefOptions {
  /** The grid's tenant picker (`?tenant=<id>`). */
  tenant?: Tenant;
  /** Extra plain params (e.g. /tags `category=`). */
  params?: Params;
}

/** `path?q=<DSL>&tenant=<id>&…` — the QuerySearch-grid deep link. */
export function queryHref(path: string, nodes: FilterNode[], opts: QueryHrefOptions = {}): string {
  const usp = new URLSearchParams();
  const q = serialize(nodes);
  if (q) usp.set('q', q);
  if (opts.tenant != null && opts.tenant !== '') usp.set('tenant', String(opts.tenant));
  appendParams(usp, opts.params);
  const qs = usp.toString();
  return qs ? `${path}?${qs}` : path;
}

// ---- QuerySearch grids ----------------------------------------------------

export const bookingsHref = (nodes: FilterNode[], tenant?: Tenant) =>
  queryHref('/bookings', nodes, { tenant });
export const ordersHref = (nodes: FilterNode[], tenant?: Tenant) =>
  queryHref('/orders', nodes, { tenant });
export const toursHref = (nodes: FilterNode[], tenant?: Tenant) =>
  queryHref('/tours', nodes, { tenant });
/** /activities is a hybrid like /tags: `?tenant=&type=<int>&category=<id>`
 *  plain params (the toolbar's own filters) PLUS `?q=`. */
export const activitiesHref = (nodes: FilterNode[], tenant?: Tenant, params?: Params) =>
  queryHref('/activities', nodes, { tenant, params });
export const extIdsHref = (nodes: FilterNode[], tenant?: Tenant) =>
  queryHref('/ext-ids', nodes, { tenant });
export const subscriptionsHref = (nodes: FilterNode[], tenant?: Tenant) =>
  queryHref('/subscriptions', nodes, { tenant });
export const apiCredentialsHref = (nodes: FilterNode[], tenant?: Tenant) =>
  queryHref('/api-credentials', nodes, { tenant });
export const importRunsHref = (nodes: FilterNode[], tenant?: Tenant) =>
  queryHref('/import-runs', nodes, { tenant });
export const notificationsHref = (nodes: FilterNode[], tenant?: Tenant) =>
  queryHref('/notifications', nodes, { tenant });
export const tourOperatorsHref = (nodes: FilterNode[]) => queryHref('/tour-operators', nodes);
export const featureFlagsHref = (nodes: FilterNode[]) =>
  queryHref('/notifications/features', nodes);
export const findReplaceHref = (nodes: FilterNode[]) =>
  queryHref('/notifications/find-replace', nodes);
/** /tags is a hybrid: `?tenant=&category=<id>` plain params PLUS `?q=`. */
export const tagsHref = (nodes: FilterNode[], tenant?: Tenant, params?: Params) =>
  queryHref('/tags', nodes, { tenant, params });

export interface WindowedHrefOptions {
  tenant?: Tenant;
  /** A `created` bound to use when `nodes` has none. Default: the grid's
   *  own 7-day window; api-requests callers usually pass
   *  `createdAroundNode(ts)` for ±1 h around a known moment. */
  window?: FilterNode;
}

function windowed(nodes: FilterNode[], fallback: FilterNode): FilterNode[] {
  return hasCreatedTimeBound(nodes) ? nodes : [...nodes, fallback];
}

/** /delivery-logs — always bounded (partition pruning is load-bearing). */
export const deliveryLogsHref = (nodes: FilterNode[], opts: WindowedHrefOptions = {}) =>
  queryHref('/delivery-logs', windowed(nodes, opts.window ?? createdWindowNode()), {
    tenant: opts.tenant,
  });

/** /api-requests — always bounded (the backend refuses a windowless search). */
export const apiRequestsHref = (nodes: FilterNode[], opts: WindowedHrefOptions = {}) =>
  queryHref('/api-requests', windowed(nodes, opts.window ?? createdWindowNode()), {
    tenant: opts.tenant,
  });

/** /external-access — always bounded (monthly partitions; the backend refuses a windowless
 *  search). A partner is `partner=#<id>` (`idCond`), a key `credential=<id>` (exact). */
export const externalAccessHref = (nodes: FilterNode[], opts: WindowedHrefOptions = {}) =>
  queryHref('/external-access', windowed(nodes, opts.window ?? createdWindowNode()), {
    tenant: opts.tenant,
  });

// ---- Plain-param grids ----------------------------------------------------

export type UserKind = 'system' | 'staff' | 'guests';

export const usersHref = (kind: UserKind, params?: Params) => plainHref(`/users/${kind}`, params);
export const sessionsHref = (params?: Params) => plainHref('/users/sessions', params);
export const auditHref = (params?: Params) => plainHref('/audit', params);
export const changesHref = (params?: Params) => plainHref('/changes', params);
/** /ai-changes — `tenant`, `actor`, `channel`, `status`, `client`, `from`/`to` (ISO),
 *  `has_conflicts`, `revert_of`, `batch_id` (the page's own URL params, not the API's). */
export const aiChangesHref = (params?: Params) => plainHref('/ai-changes', params);
export const bgtasksHref = (params?: Params) => plainHref('/bgtasks', params);
/** /support-tickets — `tenant`, `status`, `severity`, `delivery_status` (comma lists of the API's
 *  values), `from`/`to` (ISO), `booking` (a booking id: the tickets covering it). */
export const supportTicketsHref = (params?: Params) => plainHref('/support-tickets', params);
/** /platform-notices — `tenant` (the notices that apply to it), `capability`, `audience`,
 *  `active`; `edit=<id>` opens one (`useEditIntent`), `new` a new one. */
export const platformNoticesHref = (params?: Params) => plainHref('/platform-notices', params);
/** /mcp-limit-hits — `since` / `until` (UTC days, `YYYY-MM-DD`), `tenant`, `limit` (keys). */
export const mcpLimitHitsHref = (params?: Params) => plainHref('/mcp-limit-hits', params);
/** /knowledge-gaps — `since` / `until` (UTC days), `tenant` (ids or `system`), `kind`, `search`,
 *  `group` (`subject` | `tenant` | `day`), `ordering`. */
export const knowledgeGapsHref = (params?: Params) => plainHref('/knowledge-gaps', params);
/** /mcp-diagnose-runs — `tenant`, `review` (`pending` | `reviewed` | `flagged` | `none`), `reason`,
 *  `judge`, `outcome` (comma lists of the API's values), `from`/`to` (ISO or relative). */
export const mcpDiagnoseRunsHref = (params?: Params) => plainHref('/mcp-diagnose-runs', params);
export const vehiclesHref = (params?: Params) => plainHref('/vehicles', params);
export const launchPoliciesHref = (params?: Params) => plainHref('/launch-policies', params);
export const tagCategoriesHref = (params?: Params) => plainHref('/tag-categories', params);
export const activityCategoriesHref = (params?: Params) =>
  plainHref('/activity-categories', params);
export const itinerariesHref = (params?: Params) => plainHref('/itineraries', params);
export const busStopsHref = (params?: Params) => plainHref('/bus-stops', params);
export const lineSetsHref = (params?: Params) => plainHref('/line-sets', params);
export const tripTypesHref = (params?: Params) => plainHref('/trip-types', params);
export const regionsHref = (params?: Params) => plainHref('/regions', params);
export const preTripItemsHref = (params?: Params) => plainHref('/pre-trip-items', params);
export const otasHref = (params?: Params) => plainHref('/otas', params);
/** /rbac/access-groups — `?managed=` and the name / created filters; the
 *  tenant there is component state, not a URL param. */
export const accessGroupsHref = (params?: Params) => plainHref('/rbac/access-groups', params);

// ---- Composite queries ----------------------------------------------------

/** The bookings a guide's sheet for one run would list: same tour, date and
 *  departure slot, booked only. The tour goes by ID through the backend's
 *  all-digits fallback (same trick as the grids' Tour cell) so it pins THIS
 *  tour rather than every same-named one. Pair with `bookingsHref(…, tenant)`. */
export function manifestConds(b: {
  tourId?: number | null | undefined;
  date?: string | null | undefined;
  departure?: string | null | undefined;
}): FilterNode[] {
  const out: FilterNode[] = [];
  if (b.tourId != null) out.push(cond('tour', String(b.tourId)));
  if (b.date) out.push(cond('date', b.date, 'exact'));
  if (b.departure) out.push(cond('departure', b.departure));
  out.push(cond('state', 'Booked'));
  return out;
}

// ---- Record → console page ------------------------------------------------

/** Detail route (or `?edit=` intent) for a backend record, by model.
 *
 *  Accepts every spelling the backend uses for a model: the audit/changes
 *  `app_label.model` content type (`demo.group_tour`, `users.user`), the
 *  used_by `type_key` (`demo_group_tour`), or the bare model name. Returns
 *  `null` for models with no console page — callers render plain text then; a
 *  fabricated route that 404s is worse than no link (see components.md
 *  "Deep-linking a record"). */
export function consoleRouteFor(kind: string, id: number | string): string | null {
  const key = kind.toLowerCase().replace(/^(demo|users|bgtasks)[._]/, '');
  const build = MODEL_ROUTES[key];
  return build ? build(String(id)) : null;
}

const MODEL_ROUTES: Record<string, (id: string) => string> = {
  group_tour: (id) => `/tours/${id}`,
  tour_operator: (id) => `/tour-operators/${id}`,
  activity: (id) => `/activities/${id}`,
  activity_category: (id) => `/activity-categories/${id}`,
  itinerary_group: (id) => `/itineraries/${id}`,
  region: (id) => `/regions/${id}`,
  trip_type: (id) => `/trip-types/${id}`,
  pre_trip_item: (id) => `/pre-trip-items/${id}`,
  pre_trip_item_type: (id) => `/pre-trip-item-categories/${id}`,
  vehicle_route_set: (id) => `/line-sets/${id}`,
  vehicle_route_stop: (id) => `/bus-stops/${id}`,
  group_tour_tag: (id) => `/tags/${id}`,
  group_tour_tag_category: (id) => `/tag-categories/${id}`,
  group_tour_user: (id) => `/bookings/${id}`,
  order: (id) => `/orders/${id}`,
  user: (id) => `/users/${id}`,
  event_delivery_log: (id) => `/delivery-logs/${id}`,
  import_run: (id) => `/import-runs/${id}`,
  api_request: (id) => `/api-requests/${id}`,
  api_credential: (id) => `/api-credentials/${id}`,
  onboarding_invite: (id) => `/onboarding-invites/${id}`,
  integration_notification_subscription: (id) => `/subscriptions/${id}`,
  // The ext-ids page keys rows by kind:pk; `aeid` is an Additional_Ext_ID.
  additional_ext_id: (id) => `/ext-ids/aeid:${id}`,
  online_travel_agency: (id) => `/otas/${id}`,
  access_group: (id) => `/rbac/access-groups/${id}`,
  notification: (id) => `/notifications/${id}`,
  notification_ruleset: (id) => `/notifications/rulesets/${id}`,
  backgroundtaskrecord: (id) => `/bgtasks/${id}`,
  // The audit log's details dialog is URL-driven (/audit/<id>).
  console_audit_log: (id) => `/audit/${id}`,
  // Dialog-edited resources: the list page consumes `?edit=` (useEditIntent).
  notification_block: (id) => `/notifications/blocks?edit=${id}`,
  block_list: (id) => `/notifications/block-lists?edit=${id}`,
  notification_theme: (id) => `/notifications/themes?edit=${id}`,
  language_policy: (id) => `/notifications/language-policies?edit=${id}`,
  platform_notice: (id) => `/platform-notices?edit=${id}`,
};

/** The models `consoleRouteFor` knows — exported for the route-table test. */
export const CONSOLE_ROUTED_MODELS = Object.keys(MODEL_ROUTES);
